<script lang="ts">
export interface CommandProps<
	TOption,
	TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
> extends ListProps<TOption, TValueKey>,
		VirtualizerProps,
		ClassProps {
	placeholder?: string;
	hideSearch?: boolean;
}

export interface CommandEmits<TOption, TValue> {
	select: [evt: { option: TOption; value: TValue }];
}
</script>

<script
	setup
	lang="ts"
	generic="
		TOption,
		TValueKey extends KeysWithType<TOption, ListOptionAcceptableValue>,
		TValue extends ResolveValue<TOption, TValueKey>
	"
>
import { ListboxFilter, ListboxRoot } from "reka-ui";
import { type ClassProps } from "../../lib/base-props";
import { cn } from "../../lib/utils";
import {
	useListOptions,
	type KeysWithType,
	type ListOptionAcceptableValue,
	type ListProps,
	type ResolveValue,
} from "../listbox/listbox";
import type { VirtualizerProps } from "../virtualizer/virtualizer";
import CommandInput from "./CommandInput.vue";
import CommandListOptions from "./CommandListOptions.vue";

const props = defineProps<CommandProps<TOption, TValueKey>>();
const emit = defineEmits<CommandEmits<TOption, TValue>>();

const filter = defineModel<string>("filter", { default: "" });

defineSlots<{
	prepend(): void;
	append(): void;
	option(slotProps: { option: TOption }): void;
	groupLabel(slotProps: { label: string }): void;
}>();

const { filteredOptions } = useListOptions(props, filter);
</script>

<template>
	<ListboxRoot
		:class="
			cn(
				'bg-popover text-popover-content flex h-full flex-col overflow-hidden rounded-md',
				props.class
			)
		"
		selectionBehavior="toggle"
		highlightOnHover
		@entryFocus.prevent
	>
		<CommandInput v-if="!hideSearch" v-model="filter" :placeholder="props.placeholder">
			<template #prepend>
				<slot name="prepend" />
			</template>

			<template #append>
				<slot name="append" />
			</template>
		</CommandInput>

		<!-- Without a ListboxFilter, the highlight on hover don't work -->
		<ListboxFilter v-else class="hidden" />

		<CommandListOptions
			:class="props.class"
			:options="filteredOptions"
			:virtual="props.virtual"
			:overscan="props.overscan"
			:estimateHeight="props.estimateHeight"
			@select="(evt) => emit('select', evt as any)"
		>
			<template #option="slotProps">
				<slot name="option" v-bind="slotProps" />
			</template>

			<template #label="slotProps">
				<slot name="groupLabel" v-bind="slotProps" />
			</template>
		</CommandListOptions>

		<slot />
	</ListboxRoot>
</template>
